import React from "react";
import PropTypes from "prop-types";
import styles from "./index.module.css";

/**
 * Shared, theme-aware pagination control. Renders Prev / numbered / Next
 * buttons with a windowed range of page numbers (5 centered on the current
 * page) and leading/trailing ellipses. Renders nothing when there is a single
 * page. Used by the Store grid and the Order Management add-to-cart catalog.
 */
export function Pagination({
  page,
  totalPages,
  onPageChange,
  ariaLabel,
  pageSize,
  onPageSizeChange,
  pageSizeOptions,
  resultsLabel,
}) {
  const pageNumbers = React.useMemo(() => {
    const span = 5;
    const start = Math.max(1, Math.min(page - 2, totalPages - span + 1));
    const end = Math.min(totalPages, start + span - 1);
    const nums = [];
    for (let n = start; n <= end; n += 1) nums.push(n);
    return nums;
  }, [page, totalPages]);

  const showSizeSelector = typeof onPageSizeChange === "function";
  if (totalPages <= 1 && !showSizeSelector) return null;

  const go = (next) => {
    const clamped = Math.min(Math.max(1, next), totalPages);
    if (clamped !== page) onPageChange(clamped);
  };

  return (
    <div className={styles.paginationBar}>
      {totalPages > 1 && (
        <nav className={styles.pagination} aria-label={ariaLabel}>
          <button
            type="button"
            className={styles.pageBtn}
            onClick={() => go(page - 1)}
            disabled={page <= 1}
            aria-label="Previous page"
          >
            ‹ Prev
          </button>

          {pageNumbers[0] > 1 && <span className={styles.pageEllipsis}>…</span>}

          {pageNumbers.map((n) => (
            <button
              key={n}
              type="button"
              className={`${styles.pageBtn} ${
                n === page ? styles.pageBtnActive : ""
              }`}
              onClick={() => go(n)}
              aria-current={n === page ? "page" : undefined}
            >
              {n}
            </button>
          ))}

          {pageNumbers[pageNumbers.length - 1] < totalPages && (
            <span className={styles.pageEllipsis}>…</span>
          )}

          <button
            type="button"
            className={styles.pageBtn}
            onClick={() => go(page + 1)}
            disabled={page >= totalPages}
            aria-label="Next page"
          >
            Next ›
          </button>
        </nav>
      )}

      {showSizeSelector && (
        <label className={styles.pageSize}>
          <span>Per page</span>
          <select
            className={styles.pageSizeSelect}
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label="Items per page"
          >
            {pageSizeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </label>
      )}

      {resultsLabel && (
        <span className={styles.resultsLabel}>{resultsLabel}</span>
      )}
    </div>
  );
}

Pagination.propTypes = {
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
  pageSize: PropTypes.number,
  onPageSizeChange: PropTypes.func,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  resultsLabel: PropTypes.string,
};

Pagination.defaultProps = {
  ariaLabel: "Pages",
  pageSize: undefined,
  onPageSizeChange: undefined,
  pageSizeOptions: [10, 50, 100],
  resultsLabel: undefined,
};

export default Pagination;
